import * as React from "react";
import { Camera, MapPin, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Evidence capture block — attach photo + note + optional geo to a
 * shift / booking / card. Geo is opportunistic: we ask, we don't require.
 *
 * Props:
 *   companyId          — string
 *   attachedType       — 'shift' | 'booking' | 'card'
 *   attachedId         — string
 *   canEdit            — boolean (gates the "Add evidence" affordance)
 *   testidPrefix       — string, defaults to 'evidence'
 */
export default function EvidenceBlock({
  companyId, attachedType, attachedId, canEdit = true,
  testidPrefix = "evidence",
}) {
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/evidence`, {
        params: { attached_type: attachedType, attached_id: attachedId },
      });
      setRows(data.evidence || []);
    } catch (e) {
      // 403 or 404 → silently show empty; the parent drawer already knows
      // whether the caller can view it.
      setRows([]);
    }
  }, [companyId, attachedType, attachedId]);

  React.useEffect(() => { if (attachedId) load(); }, [load, attachedId]);

  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type;
      if (!t) return;
      if (t === "evidence.captured" || t === "evidence.deleted") {
        const p = e.detail?.payload;
        if (!p) return;
        if (p.attached_type === attachedType && p.attached_id === attachedId) load();
      }
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [attachedType, attachedId, load]);

  async function upload(file, note) {
    if (!file || busy) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("attached_type", attachedType);
    fd.append("attached_id", attachedId);
    fd.append("photo", file);
    if (note) fd.append("note", note);
    // Try geolocation but never require it
    try {
      const geo = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (p) => res({
            lat: p.coords.latitude, lng: p.coords.longitude,
            acc: p.coords.accuracy,
          }),
          () => res(null),
          { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 },
        );
      });
      if (geo) {
        fd.append("geo_lat", String(geo.lat));
        fd.append("geo_lng", String(geo.lng));
        if (geo.acc != null) fd.append("geo_accuracy_m", String(geo.acc));
      }
    } catch { /* ignore — geo is optional */ }

    try {
      await api.post(`/companies/${companyId}/evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidence captured");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this evidence?")) return;
    try {
      await api.delete(`/companies/${companyId}/evidence/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <div className="rounded-md border border-brand-rule p-3 space-y-3" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between">
        <div className="micro-label flex items-center gap-1"><Camera size={11}/> Evidence</div>
        <div className="mono-num text-[10.5px] text-brand-muted">
          {rows === null ? "…" : `${rows.length}`}
        </div>
      </div>

      {canEdit && (
        <EvidencePicker onFile={upload} busy={busy} testidPrefix={testidPrefix} />
      )}

      {rows === null && (
        <div className="text-[11px] text-brand-muted">Loading…</div>
      )}

      {rows && rows.length === 0 && (
        <div className="text-[11px] text-brand-faint">No evidence yet.</div>
      )}

      {rows && rows.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {rows.map((r) => (
            <div key={r.id} className="group relative rounded-md border border-brand-rule overflow-hidden bg-brand-canvas"
                 data-testid={`${testidPrefix}-item-${r.id}`}>
              <button type="button" onClick={() => setPreview(r)}
                      className="block w-full aspect-square bg-brand-canvas">
                <img
                  src={`${api.defaults.baseURL}/companies/${companyId}/evidence/${r.id}/photo`}
                  alt="evidence"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
              <div className="px-1.5 py-1 text-[10px] mono-num text-brand-muted flex items-center gap-1">
                <span className="truncate flex-1">{(r.captured_at || "").slice(5, 16).replace("T", " ")}</span>
                {r.geo && <MapPin size={9} className="text-brand-teal" aria-label="geo captured" />}
              </div>
              {r.note && (
                <div className="px-1.5 pb-1 text-[10px] text-brand-ink truncate">{r.note}</div>
              )}
              {canEdit && (
                <button type="button"
                        onClick={() => remove(r.id)}
                        className="absolute top-1 right-1 h-6 w-6 rounded-md bg-card/90 border border-brand-rule text-brand-coral opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        data-testid={`${testidPrefix}-delete-${r.id}`}
                        aria-label="Delete evidence">
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <PreviewLightbox
          row={preview}
          onClose={() => setPreview(null)}
          companyId={companyId}
        />
      )}
    </div>
  );
}

function EvidencePicker({ onFile, busy, testidPrefix }) {
  const inputRef = React.useRef(null);
  const [note, setNote] = React.useState("");
  const [file, setFile] = React.useState(null);

  function onPick(e) {
    const f = e.target.files?.[0] || null;
    setFile(f);
  }

  function submit(e) {
    e?.preventDefault?.();
    if (!file) return;
    onFile(file, note);
    setNote(""); setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid={`${testidPrefix}-form`}>
      <div className="flex items-center gap-2">
        <label className="btn-outline !h-8 !px-2 cursor-pointer">
          <Upload size={12} />
          <span>{file ? file.name.slice(0, 22) : "Choose photo"}</span>
          <input ref={inputRef} type="file" accept="image/*" capture="environment"
                 className="hidden" onChange={onPick}
                 data-testid={`${testidPrefix}-file`} />
        </label>
        <input className="field-input flex-1 !h-8 !text-[12px]"
               placeholder="Note (optional)"
               value={note} onChange={(e) => setNote(e.target.value)}
               data-testid={`${testidPrefix}-note`} />
        <button type="submit" className="btn-violet !h-8"
                disabled={!file || busy}
                data-testid={`${testidPrefix}-submit`}>
          {busy ? "…" : "Capture"}
        </button>
      </div>
      <div className="text-[10.5px] text-brand-faint">
        Location included if your browser allows it, never required.
      </div>
    </form>
  );
}

function PreviewLightbox({ row, onClose, companyId }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="evidence-lightbox">
      <div className="max-w-3xl w-full bg-card rounded-lg overflow-hidden shadow-card"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-2 border-b border-brand-rule flex items-center justify-between">
          <div className="text-[12px] text-brand-ink">
            <span className="mono-num">{(row.captured_at || "").slice(0, 16).replace("T", " ")}</span>
            <span className="mx-2 text-brand-muted">·</span>
            <span className="text-brand-muted">by {row.captured_by_name || "—"}</span>
            {row.geo && (
              <span className="ml-2 chip mono-num text-[10px]">
                <MapPin size={9}/> {row.geo.lat.toFixed(4)}, {row.geo.lng.toFixed(4)}
              </span>
            )}
          </div>
          <button className="btn-ghost !h-8 !w-8 !p-0" onClick={onClose}>
            <X size={14}/>
          </button>
        </div>
        <img
          src={`${api.defaults.baseURL}/companies/${companyId}/evidence/${row.id}/photo`}
          className="max-h-[70vh] w-full object-contain bg-black/5"
          alt="evidence"
        />
        {row.note && (
          <div className="p-4 border-t border-brand-rule text-[13px] text-brand-ink whitespace-pre-wrap">
            {row.note}
          </div>
        )}
      </div>
    </div>
  );
}
